import { jsPDF } from "jspdf";
import moment from "moment";

const EXPORT_STATUSES = new Set([
  "accepted",
  "startduty",
  "endduty",
  "completed",
]);

const normalizeStatus = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const textValue = (value, fallback = "-") => {
  if (value === 0) return "0";
  if (value === false) return "No";
  if (value === true) return "Yes";
  if (value == null) return fallback;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || fallback;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : fallback;
  }
  return fallback;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY hh:mm A") : "-";
};

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "-";
};

const formatAmount = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return `Rs. ${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(num)}`;
};

const safeFilePart = (value, fallback = "export") =>
  String(value || fallback)
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase() || fallback;

const getVisitId = (visit) => {
  if (!visit) return "";
  if (typeof visit._id === "string") return visit._id;
  if (visit._id?.$oid) return String(visit._id.$oid);
  return String(visit._id || visit.id || "");
};

export const getPartnerIdForExport = (visit) => {
  const rawId = visit?.appointmentType?.partnerID || visit?.partnerID || "";
  if (typeof rawId === "string") return rawId;
  if (rawId && typeof rawId === "object") {
    return String(rawId?._id || rawId?.id || rawId?.$oid || "");
  }
  return String(rawId || "");
};

const getPatientName = (visit) =>
  textValue(
    visit?.patient?.name ||
      visit?.patientInfo?.personalInfo?.basicInfo?.nameInfo?.fullName,
    "Unknown Patient",
  );

const getSpecialistName = (visit) =>
  textValue(
    visit?.dutyStaff?.name ||
      visit?.specialistInfo?.name ||
      visit?.assessmentStaff?.name,
    "Unassigned",
  );

const getPaymentModeLabel = (visit) => {
  const mode = String(visit?.paymentMode || "")
    .trim()
    .toLowerCase();
  if (mode === "online") return "Online";
  if (mode === "location_qr") return "On Location QR";
  if (mode === "daily_qr") return "Daily QR";
  if (visit?.payOnLocation === true) return "On Location QR";
  return textValue(visit?.paymentMode, "-");
};

const getStatusLabel = (status) => {
  const key = normalizeStatus(status);
  if (key === "accepted") return "Accepted / Confirmed";
  if (key === "startduty") return "Start Duty";
  if (key === "endduty") return "End Duty";
  if (key === "completed") return "Completed";
  return textValue(status, "-");
};

const getBookedByLabel = (bookedBy) => {
  const value = textValue(bookedBy);
  return normalizeStatus(value) === "self" ? "patient" : value;
};

const getCreatedAt = (visit) => {
  const timelineCreated = Array.isArray(visit?.timeline)
    ? visit.timeline.find((item) =>
        /created|booked/i.test(String(item?.event || "")),
      )
    : null;
  return timelineCreated?.dateTime || visit?.createdAt || null;
};

const getPaidInfo = (visit) => {
  const paidInfo = visit?.paymentInfo?.paidInfo || {};
  const assessmentPaid = visit?.paymentInfo?.assessment?.paidInfo || {};
  const locationQr = visit?.paymentInfo?.locationQr || {};

  const paid =
    paidInfo?.paid === true ||
    assessmentPaid?.paid === true ||
    String(locationQr?.status || "")
      .trim()
      .toLowerCase() === "paid";

  const paidDateTime =
    paidInfo?.paidDateTime ||
    assessmentPaid?.paidDateTime ||
    locationQr?.paidDateTime ||
    null;

  const paymentId =
    paidInfo?.metadata?.razorpay_payment_id ||
    assessmentPaid?.metadata?.razorpay_payment_id ||
    locationQr?.paymentID ||
    "";

  const referenceId =
    paidInfo?.metadata?.razorpay_payment_link_reference_id ||
    assessmentPaid?.metadata?.razorpay_payment_link_reference_id ||
    locationQr?.qrCodeID ||
    "";

  const amountPaid =
    paidInfo?.amountPaid ??
    assessmentPaid?.amountPaid ??
    visit?.paymentInfo?.grandTotal ??
    visit?.paymentInfo?.assessment?.grandTotal ??
    locationQr?.amount ??
    null;

  return {
    paid,
    paidDateTime,
    paymentId,
    referenceId,
    amountPaid,
    walletDiscountedAmount:
      paidInfo?.walletDiscountedAmount ??
      assessmentPaid?.walletDiscountedAmount ??
      0,
  };
};

const formatLocation = (location) => {
  if (!location || typeof location !== "object") return "-";
  if (location.formattedAddress) return textValue(location.formattedAddress);
  const parts = [
    location.name,
    location.streetNumber,
    location.street,
    location.district,
    location.city,
    location.region,
    location.state,
    location.postalCode || location.pincode,
    location.country,
  ]
    .map((part) => textValue(part, ""))
    .filter(Boolean);
  if (parts.length) return parts.join(", ");
  if (location?.coordinates?.lat != null && location?.coordinates?.lng != null) {
    return `${location.coordinates.lat}, ${location.coordinates.lng}`;
  }
  return "-";
};

const formatServiceValues = (values) => {
  if (values == null) return "-";
  if (typeof values === "string" || typeof values === "number") {
    return textValue(values);
  }
  if (Array.isArray(values)) {
    const parts = values
      .map((item) => {
        if (item == null) return "";
        if (typeof item === "string" || typeof item === "number") {
          return textValue(item, "");
        }
        if (typeof item === "object") {
          return Object.entries(item)
            .map(([key, value]) => {
              if (value == null || value === "") return "";
              if (typeof value === "object") {
                try {
                  return `${key}: ${JSON.stringify(value)}`;
                } catch (error) {
                  return `${key}: [object]`;
                }
              }
              return `${key}: ${value}`;
            })
            .filter(Boolean)
            .join(", ");
        }
        return "";
      })
      .filter(Boolean);
    return parts.length ? parts.join(" | ") : "-";
  }
  if (typeof values === "object") {
    const parts = Object.entries(values)
      .map(([key, value]) => {
        if (value == null || value === "") return "";
        if (typeof value === "object") {
          try {
            return `${key}: ${JSON.stringify(value)}`;
          } catch (error) {
            return `${key}: [object]`;
          }
        }
        return `${key}: ${value}`;
      })
      .filter(Boolean);
    return parts.length ? parts.join(", ") : "-";
  }
  return "-";
};

const getPayoutSummary = (duty) => {
  const vendor = duty?.payout?.payoutToTheVendor || {};
  const company = duty?.payout?.commisionToTheCompany || {};
  return {
    overallStatus: textValue(duty?.payout?.status, "pending"),
    vendorAmount: vendor?.amountToTransfer ?? vendor?.netPayout ?? null,
    vendorStatus: textValue(vendor?.status, "-"),
    vendorTransferId: textValue(vendor?.transferID, "-"),
    vendorTransferAt: textValue(vendor?.transferDateTime, "-"),
    vendorBonus: vendor?.bonus ?? 0,
    vendorPenalty: vendor?.penalty ?? 0,
    companyAmount: company?.netCommission ?? null,
    companyStatus: textValue(company?.status, "-"),
  };
};

const uniqueVisits = (visits = []) => {
  const map = new Map();
  (Array.isArray(visits) ? visits : []).forEach((item) => {
    const visit = item?.parentVisit || item;
    if (!visit) return;
    const id = getVisitId(visit);
    if (!id) return;
    if (!map.has(id)) map.set(id, visit);
  });
  return Array.from(map.values());
};

const getPartnerDetails = (partner, visits) => {
  const fallbackName = visits
    .map((visit) => textValue(visit?.appointmentType?.partnerName, ""))
    .find(Boolean);
  const addressInfo = partner?.addressInfo || {};
  const fullAddress =
    textValue(addressInfo?.fullAddress, "") ||
    [
      addressInfo?.address1,
      addressInfo?.address2,
      addressInfo?.city,
      addressInfo?.state,
      addressInfo?.pincode,
    ]
      .map((part) => textValue(part, ""))
      .filter(Boolean)
      .join(", ");
  const phones = [
    partner?.basicInfo?.phone,
    ...(Array.isArray(partner?.contactInfo)
      ? partner.contactInfo.map((contact) => contact?.phone)
      : []),
  ]
    .map((phone) => textValue(phone, ""))
    .filter(Boolean);

  return {
    name: textValue(partner?.basicInfo?.name || fallbackName, "Partner"),
    bannerImage: textValue(partner?.basicInfo?.bannerImage, ""),
    address: fullAddress,
    phones: Array.from(new Set(phones)).join(", "),
    gstin: textValue(partner?.verificationDocuments?.GST?.number, ""),
  };
};

const fetchImageAsDataUrl = async (src) => {
  try {
    const response = await fetch(src);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    return null;
  }
};

const getImageDimensions = (src) =>
  new Promise((resolve) => {
    const image = new Image();
    image.onload = () =>
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => resolve(null);
    image.src = src;
  });

const loadImageForPdf = async (src) => {
  const imageSrc = textValue(src, "");
  if (!imageSrc) return null;
  const dataUrl = /^data:image\//i.test(imageSrc)
    ? imageSrc
    : await fetchImageAsDataUrl(imageSrc);
  if (!dataUrl) return null;
  const dimensions = await getImageDimensions(dataUrl);
  return {
    dataUrl,
    format: dataUrl.toLowerCase().includes("image/png") ? "PNG" : "JPEG",
    width: dimensions?.width || 100,
    height: dimensions?.height || 50,
  };
};

const getExportSummary = (visits) => {
  let totalDutyDays = 0;
  let totalPayout = 0;
  let completedBookings = 0;

  visits.forEach((visit) => {
    if (normalizeStatus(visit?.status) === "completed") completedBookings += 1;
    const duties = Array.isArray(visit?.dutyInfo) ? visit.dutyInfo : [];
    totalDutyDays += duties.length;
    duties.forEach((duty) => {
      const payout = getPayoutSummary(duty).vendorAmount;
      const amount = Number(payout);
      if (Number.isFinite(amount)) totalPayout += amount;
    });
  });

  return {
    totalBookings: visits.length,
    completedBookings,
    totalDutyDays,
    totalPayout,
  };
};

export const filterExportableBookings = (visits = []) =>
  uniqueVisits(visits).filter((visit) =>
    EXPORT_STATUSES.has(normalizeStatus(visit?.status)),
  );

const ensureSpace = (doc, currentY, needed = 40) => {
  const pageHeight = doc.internal.pageSize.getHeight();
  if (currentY + needed <= pageHeight - 36) return currentY;
  doc.addPage();
  return 40;
};

const drawSectionTitle = (doc, title, y) => {
  const nextY = ensureSpace(doc, y + 8, 24);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(30, 58, 95);
  doc.text(title, 40, nextY);
  doc.setDrawColor(67, 97, 238);
  doc.setLineWidth(0.6);
  doc.line(40, nextY + 4, 555, nextY + 4);
  doc.setTextColor(20, 20, 20);
  return nextY + 16;
};

const drawKeyValueRows = (doc, rows, startY) => {
  let y = startY;
  doc.setFontSize(9);
  rows.forEach(([label, value], index) => {
    const lines = doc.splitTextToSize(textValue(value), 382);
    const rowHeight = Math.max(18, lines.length * 10 + 8);
    y = ensureSpace(doc, y, rowHeight);
    doc.setDrawColor(203, 213, 225);
    doc.setLineWidth(0.4);
    doc.setFillColor(
      index % 2 === 0 ? 248 : 255,
      index % 2 === 0 ? 250 : 255,
      index % 2 === 0 ? 252 : 255,
    );
    doc.rect(40, y - 4, 515, rowHeight, "FD");
    doc.line(160, y - 4, 160, y - 4 + rowHeight);
    doc.setFont("helvetica", "bold");
    doc.text(`${label}:`, 46, y + 7);
    doc.setFont("helvetica", "normal");
    doc.text(lines, 168, y + 7);
    y += rowHeight;
  });
  return y + 8;
};

const drawPartnerHeader = async (doc, partnerDetails, startY) => {
  let y = startY;
  const centerX = doc.internal.pageSize.getWidth() / 2;
  const logo = await loadImageForPdf(partnerDetails.bannerImage);

  if (logo?.dataUrl) {
    const logoHeight = 64;
    const logoWidth = Math.min(
      200,
      Math.max(100, (logo.width / Math.max(logo.height, 1)) * logoHeight),
    );
    doc.addImage(
      logo.dataUrl,
      logo.format,
      centerX - logoWidth / 2,
      y,
      logoWidth,
      logoHeight,
    );
    y += logoHeight + 10;
  }

  doc.setTextColor(20, 20, 20);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(17);
  doc.text(partnerDetails.name, centerX, y, { align: "center" });
  y += 15;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const detailLines = [];
  if (partnerDetails.address) {
    detailLines.push(...doc.splitTextToSize(partnerDetails.address, 430));
  }
  if (partnerDetails.phones) detailLines.push(`Phone: ${partnerDetails.phones}`);
  if (partnerDetails.gstin) detailLines.push(`GSTIN: ${partnerDetails.gstin}`);
  if (detailLines.length) {
    doc.text(detailLines, centerX, y, { align: "center" });
    y += detailLines.length * 11 + 4;
  }

  doc.setDrawColor(112, 95, 64);
  doc.setLineWidth(0.7);
  doc.line(32, y, 563, y);
  return y + 20;
};

const drawSummaryCards = (doc, summary, startY) => {
  const cards = [
    ["Total Bookings", String(summary.totalBookings)],
    ["Total Payout", formatAmount(summary.totalPayout)],
    ["Duty Days", String(summary.totalDutyDays)],
    ["Completed", String(summary.completedBookings)],
  ];
  const left = 40;
  const gap = 8;
  const cardWidth = (515 - gap * 3) / 4;
  const cardHeight = 48;

  cards.forEach(([label, value], index) => {
    const x = left + index * (cardWidth + gap);
    doc.setFillColor(238, 242, 255);
    doc.setDrawColor(199, 210, 254);
    doc.roundedRect(x, startY, cardWidth, cardHeight, 4, 4, "FD");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.text(label, x + 8, startY + 15);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(30, 58, 95);
    doc.text(value, x + 8, startY + 35);
  });

  doc.setTextColor(20, 20, 20);
  return startY + cardHeight + 20;
};

const drawSimpleTable = (doc, headers, rows, startY, colWidths) => {
  let y = ensureSpace(doc, startY, 28);
  const left = 44;
  const rowPad = 4;
  const fontSize = 8;
  const usableWidth = colWidths.reduce((sum, width) => sum + width, 0);

  const drawRow = (cells, isHeader = false) => {
    const wrapped = cells.map((cell, index) =>
      doc.splitTextToSize(textValue(cell), Math.max(colWidths[index] - 8, 20)),
    );
    const rowHeight =
      Math.max(...wrapped.map((lines) => lines.length), 1) * 10 + rowPad * 2;

    y = ensureSpace(doc, y, rowHeight + 2);
    doc.setDrawColor(203, 213, 225);
    doc.setFillColor(isHeader ? 67 : 255, isHeader ? 97 : 255, isHeader ? 238 : 255);
    doc.rect(left, y, usableWidth, rowHeight, isHeader ? "FD" : "S");

    let x = left;
    colWidths.forEach((width, index) => {
      if (index > 0) {
        doc.line(x, y, x, y + rowHeight);
      }
      doc.setFont("helvetica", isHeader ? "bold" : "normal");
      doc.setFontSize(fontSize);
      doc.setTextColor(isHeader ? 255 : 20, isHeader ? 255 : 20, isHeader ? 255 : 20);
      doc.text(wrapped[index], x + 4, y + rowPad + 8);
      x += width;
    });

    y += rowHeight;
  };

  drawRow(headers, true);
  if (!rows.length) {
    drawRow(headers.map((_, index) => (index === 0 ? "-" : index === 1 ? "No records" : "-")));
  } else {
    rows.forEach((row) => drawRow(row));
  }

  doc.setTextColor(20, 20, 20);
  return y + 10;
};

const drawVisitBlock = (doc, visit, index, startY) => {
  let y = ensureSpace(doc, startY, 48);
  const paidInfo = getPaidInfo(visit);
  const services = Array.isArray(visit?.visitInfo?.services)
    ? visit.visitInfo.services
        .map((service) =>
          typeof service === "string"
            ? service
            : service?.name || service?.service || "",
        )
        .filter(Boolean)
        .join(", ")
    : "-";

  doc.setFillColor(238, 242, 255);
  doc.roundedRect(36, y - 12, 523, 28, 3, 3, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(30, 58, 95);
  doc.text(
    `${index + 1}. ${getPatientName(visit)}  |  Visit ID: ${textValue(visit?.jobID, "-")}`,
    44,
    y + 6,
  );
  doc.setTextColor(20, 20, 20);
  y += 28;

  y = drawSectionTitle(doc, "Booking Overview", y);
  y = drawKeyValueRows(
    doc,
    [
      ["Visit Status", getStatusLabel(visit?.status)],
      ["Booking Created", formatDateTime(getCreatedAt(visit))],
      [
        "Service Window",
        `${formatDate(visit?.visitInfo?.startDate)} - ${formatDate(
          visit?.visitInfo?.endDate,
        )}`,
      ],
      ["Duty Type", textValue(visit?.dutyType)],
      [
        "Shift / Slot",
        textValue(visit?.visitInfo?.shiftType || visit?.visitInfo?.slot),
      ],
      ["Specialist Role", textValue(visit?.specialistInfo?.role)],
      ["Assigned HCP", getSpecialistName(visit)],
      ["Standby HCP", textValue(visit?.standBySpecialist?.name)],
      ["Booked By", getBookedByLabel(visit?.bookedBy)],
      ["Services", services || "-"],
      [
        "Address",
        [
          visit?.visitInfo?.addressInfo?.address1,
          visit?.visitInfo?.addressInfo?.address2,
          visit?.visitInfo?.addressInfo?.landmark,
          visit?.visitInfo?.addressInfo?.city,
          visit?.visitInfo?.addressInfo?.district,
          visit?.visitInfo?.addressInfo?.state,
          visit?.visitInfo?.addressInfo?.pincode,
        ]
          .map((part) => textValue(part, ""))
          .filter(Boolean)
          .join(", ") || "-",
      ],
    ],
    y,
  );

  y = drawSectionTitle(doc, "Payment Details", y);
  y = drawKeyValueRows(
    doc,
    [
      ["Payment Mode", getPaymentModeLabel(visit)],
      ["Paid", paidInfo.paid ? "Yes" : "No"],
      ["Paid On", formatDateTime(paidInfo.paidDateTime)],
      ["Amount", formatAmount(paidInfo.amountPaid)],
      ["Wallet Discount", formatAmount(paidInfo.walletDiscountedAmount)],
      ["Fee / Day", formatAmount(visit?.paymentInfo?.feePerDay)],
      ["Total Fees", formatAmount(visit?.paymentInfo?.totalFees)],
      ["GST", formatAmount(visit?.paymentInfo?.gst)],
      ["Platform Fee", formatAmount(visit?.paymentInfo?.platformFee)],
      ["Grand Total", formatAmount(visit?.paymentInfo?.grandTotal)],
      ["Payment ID", textValue(paidInfo.paymentId)],
      ["Payment Reference", textValue(paidInfo.referenceId)],
      ["Location QR Status", textValue(visit?.paymentInfo?.locationQr?.status)],
      [
        "Location QR Paid On",
        formatDateTime(visit?.paymentInfo?.locationQr?.paidDateTime),
      ],
    ],
    y,
  );

  const duties = Array.isArray(visit?.dutyInfo) ? visit.dutyInfo : [];
  y = drawSectionTitle(doc, `Day-wise Duty Details (${duties.length})`, y);

  if (!duties.length) {
    y = drawKeyValueRows(doc, [["Duties", "No duty days available"]], y);
  } else {
    duties.forEach((duty, dutyIndex) => {
      const payout = getPayoutSummary(duty);
      const dayLabel = `Day ${duty?.day || dutyIndex + 1}  |  ${textValue(
        duty?.date,
        "-",
      )}`;

      y = ensureSpace(doc, dutyIndex > 0 ? y + 14 : y, 20);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(29, 78, 216);
      doc.text(dayLabel, 44, y);
      doc.setTextColor(20, 20, 20);
      y += 12;

      const dutyRows = [
        ["Service Status", textValue(duty?.serviceStatus)],
        ["Scheduled Start", formatDateTime(duty?.startTime)],
        ["Scheduled End", formatDateTime(duty?.endTime)],
        ["Check-in Verified", duty?.startDuty?.verified ? "Yes" : "No"],
        ["Check-in Time", formatDateTime(duty?.startDuty?.time)],
        ["Check-in Location", formatLocation(duty?.startDuty?.location)],
        ["Check-out Verified", duty?.endDuty?.verified ? "Yes" : "No"],
        ["Check-out Time", formatDateTime(duty?.endDuty?.time)],
        ["Check-out Location", formatLocation(duty?.endDuty?.location)],
        ["Payout Status", payout.overallStatus],
        [
          "Vendor Payout",
          `${formatAmount(payout.vendorAmount)} (${payout.vendorStatus})`,
        ],
        ["Vendor Transfer ID", payout.vendorTransferId],
        ["Vendor Transfer At", payout.vendorTransferAt],
        ["Vendor Bonus", formatAmount(payout.vendorBonus)],
        ["Vendor Penalty", formatAmount(payout.vendorPenalty)],
        [
          "Company Commission",
          `${formatAmount(payout.companyAmount)} (${payout.companyStatus})`,
        ],
      ];
      if (normalizeStatus(duty?.payment?.status) !== "notrequired") {
        dutyRows.splice(9, 0, [
          "Daily Payment",
          `${textValue(duty?.payment?.status)} / ${formatAmount(
            duty?.payment?.amount,
          )} / ${formatDateTime(duty?.payment?.paidDateTime)}`,
        ]);
      }
      y = drawKeyValueRows(doc, dutyRows, y);

      const serviceRows = (
        Array.isArray(duty?.services) ? duty.services : []
      ).map((service, serviceIndex) => [
        String(serviceIndex + 1),
        textValue(service?.name, "Service"),
        formatDateTime(service?.dateTime),
        formatServiceValues(service?.values ?? service?.value),
      ]);

      y = ensureSpace(doc, y + 10, 26);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.text("Service Reports", 44, y);
      y += 12;

      y = drawSimpleTable(
        doc,
        ["#", "Service", "Reported At", "Details"],
        serviceRows,
        y,
        [24, 100, 100, 259],
      );
    });
  }

  const timeline = Array.isArray(visit?.timeline) ? visit.timeline : [];
  y = drawSectionTitle(doc, `Timeline (${timeline.length})`, y);
  if (!timeline.length) {
    y = drawKeyValueRows(doc, [["Timeline", "No timeline events"]], y);
  } else {
    const timelineRows = timeline.map((item, timelineIndex) => [
      String(timelineIndex + 1),
      textValue(item?.event),
      formatDateTime(item?.dateTime),
      textValue(item?.description),
      textValue(
        [item?.actor?.name, item?.actor?.role].filter(Boolean).join(" / "),
      ),
    ]);

    y = drawSimpleTable(
      doc,
      ["#", "Event", "When", "Description", "Actor"],
      timelineRows,
      y,
      [22, 90, 90, 210, 71],
    );
  }

  return y + 10;
};

export const downloadDetailedBookingsPdf = async ({
  visits = [],
  partner = null,
  sourceLabel = "Bookings",
} = {}) => {
  const exportVisits = filterExportableBookings(visits);

  if (!exportVisits.length) {
    const error = new Error(
      "No accepted, startDuty, endDuty, or completed bookings found for export.",
    );
    error.code = "NO_EXPORT_DATA";
    throw error;
  }

  const sortedVisits = [...exportVisits].sort((left, right) => {
    const leftTime = new Date(getCreatedAt(left) || 0).getTime();
    const rightTime = new Date(getCreatedAt(right) || 0).getTime();
    return rightTime - leftTime;
  });

  const doc = new jsPDF({
    unit: "pt",
    format: "a4",
  });

  const generatedAt = moment().format("DD/MM/YYYY hh:mm A");
  const partnerDetails = getPartnerDetails(partner, sortedVisits);
  const summary = getExportSummary(sortedVisits);
  let y = await drawPartnerHeader(doc, partnerDetails, 34);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(30, 58, 95);
  doc.text("Detailed Booking Export", 40, y);
  y += 18;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(55, 65, 81);
  doc.text(`Generated At: ${generatedAt}`, 40, y);
  y += 22;
  y = drawSummaryCards(doc, summary, y);

  sortedVisits.forEach((visit, index) => {
    y = drawVisitBlock(doc, visit, index, y);
    if (index < sortedVisits.length - 1) {
      y = ensureSpace(doc, y, 30);
      doc.setDrawColor(226, 232, 240);
      doc.line(40, y, 555, y);
      y += 18;
    }
  });

  const pageCount = doc.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text(
      `Page ${page} of ${pageCount}  |  Ceviliyar Detailed Booking Export`,
      40,
      doc.internal.pageSize.getHeight() - 18,
    );
  }

  const fileName = `${safeFilePart(sourceLabel, "bookings")}-detailed-${moment().format(
    "YYYYMMDD-HHmmss",
  )}.pdf`;
  doc.save(fileName);

  return {
    count: sortedVisits.length,
    fileName,
  };
};

export default downloadDetailedBookingsPdf;
